import { useEffect, useState, useMemo } from 'react'
import PremiumLocked from '../../../Items/Lock'
import MDBox from '../../../MDComponents/MDBox'
import MDButton from '../../../MDComponents/MDButton'
import GridSkeleton from '../GridSkeleton'
import Latex from 'react-latex-next'
import 'katex/dist/katex.min.css'
import { useMediaQuery } from 'react-responsive'

import CustomTour from 'components/CustomTour'
import pages from 'constants/pages'

import {
  maxCharachterWidth,
  renderHtml,
  getHighlighedCode,
  formatExplanation,
} from '../../../../utils'
import { NavLink } from 'react-router-dom'
import Button from '../../../Button'
import ChoiceInput from '../../../ChoiceInput'
import { useTourContext } from '../../../../context/TourContext'
import { useAuthContext } from 'context'
import {
  FilterDialog,
  UnavailableFilterSpan,
} from '../Modals/FilterModal/FilterDialog'
import useGetFilters from 'hooks/useGetAllTags'
import { settings } from 'settings'

const Content = ({
  isCreateSubject,
  showSkeleton,
  currentGrila,
  isSubmitted,
  answer,
  isMultipleChoice,
  isGettingNextGrid,
  endTrial,
  refference,
  started,
  trialGrileCount,
  justStarted,
  handleStartNow,
  handleChangeAnswer,
  isSubject,
  selectedAnswerIndex,
  screenSize,
  currentGridIndex,
  userChoice,
  isLoading,
  marginBottomValue,
  isSubmittingGrid,
  explainAnswers,
  handleChangeChaptersByCourse,
  handleSelectDifficulty,
  filterDifficulty,
  hasSubscription,
  filteredTags,
  handleTagsCheckboxChange,
  grileTypes,
  handleChangeGrileTypes,
  solvingModes,
  handleChangeSolvingMode,
  books,
  book,
}) => {
  const { handleNextTourStep, setStepToSelector } = useTourContext()
  const { userDetails } = useAuthContext()

  const mappedCurrentGrid = useMemo(() => {
    return currentGrila?.map((item, index) => {
      if (item.type === 'text') {
        return (
          <div
            key={`text-${index}`}
            className={`md:text-[18px] text-over-ternary-background text-[16px] ${
              index >= 1 && `mt-[10px]`
            }`}
          >
            <Latex className="leading-6"> {renderHtml(item.content)} </Latex>
          </div>
        )
      } else if (item.type === 'code') {
        return (
          <pre key={`code-${index}`} className="mt-[20px] mb-[20px]">
            <div
              className="md:text-[16px] text-[14px] cpp hljs text-over-ternary-background"
              dangerouslySetInnerHTML={{
                __html: getHighlighedCode(item?.content),
              }}
            ></div>
          </pre>
        )
      } else if (item.type === 'matrix') {
        const numberOfChoices = Math.floor(
          (screenSize.width - 20) / (maxCharachterWidth * item.maxSize),
        )
        return (
          <div
            className="mt-[20px] grid mb-[20px] gap-y-[20px] gap-x-[30px]"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                numberOfChoices,
                item?.content?.length,
              )}, 1fr)`,
              color: 'red',
            }}
            key={`matrix-${index}`}
          >
            <pre key={`matrix-${index}`}>
              <div className="text-[14px] cpp hljs text-over-ternary-background">
                {item.content}
              </div>
            </pre>
          </div>
        )
      } else if (item.type === 'image') {
        return (
          <div key={`image-${index}`} className="grid justify-items-stretch">
            <img
              className="mt-[10px] justify-self-center "
              src={item.content}
            />
          </div>
        )
      }
    })
  }, [currentGrila, screenSize.width])

  const gridsAnswers = useMemo(() => {
    return currentGrila?.filter(gridAnswer => {
      return (
        typeof gridAnswer?.type === 'string' &&
        gridAnswer.type.includes('choice') &&
        gridAnswer.content?.length > 0
      )
    })
  }, [currentGrila])

  const mappedGridAnswers = useMemo(() => {
    return gridsAnswers?.map((item, index) => {
      // Only show check mark on user-selected answers
      const checked = isMultipleChoice
        ? String(selectedAnswerIndex).includes(String(index))
        : index === selectedAnswerIndex

      // Determine if this option is part of the correct answer
      const isCorrectAnswer = isMultipleChoice
        ? String(
            isSubject
              ? answer?.correct_answer[currentGridIndex]
              : answer?.correct_answer,
          ).includes(String(index))
        : isCreateSubject || isSubject
          ? index === answer?.correct_answer?.[currentGridIndex]
          : index === answer?.correct_answer

      return (
        <div
          className={`w-full ${answer && 'p-4'} ${
            answer && // Only show background colors after answer is submitted
            (isCorrectAnswer
              ? 'bg-correct' // Correct answer - show green
              : checked
                ? 'bg-error' // Wrong selected answer - show red
                : '')
          }`}
          key={item.type}
        >
          <div
            key={`choice-container-${index}`}
            className={`flex ${
              !item?.type?.includes('code') && !item?.type?.includes('matrix')
                ? 'items-center'
                : 'items-start'
            } gap-3 w-full ${
              answer !== null ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={
              answer === null
                ? () => {
                    handleChangeAnswer(index)
                    if (!userDetails?.tutorial) {
                      handleNextTourStep()
                    }
                  }
                : undefined
            }
          >
            <ChoiceInput
              key={`choice-container-${index}`}
              checked={checked}
              choice={String.fromCharCode(index + 65)}
              isMultipleChoice={isMultipleChoice}
              disabled={answer !== null}
              className={checked && '!bg-primary !text-white'}
            />
            <label
              htmlFor={`choice-${index}`}
              className={`flex items-center text-over-ternary-background ${
                answer === null ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="flex items-center">
                {item?.type?.includes('code') && (
                  <pre>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getHighlighedCode(item?.content),
                      }}
                      className="text-[16px] cpp"
                    ></div>
                  </pre>
                )}
                {item?.type?.includes('matrix') && (
                  <pre>
                    <div className="tracking-[0.2px] pl-[4px] text-[14px] md:pl[10px] md:tracking-[1px] cpp">
                      {item.content}
                    </div>
                  </pre>
                )}
                {!item?.type?.includes('code') &&
                  !item?.type?.includes('matrix') && (
                    <Latex className="text-[14px] tracking-[0] pl-[7px]">
                      {' '}
                      {renderHtml(item.content)}{' '}
                    </Latex>
                  )}
              </div>
            </label>
          </div>
          {explainAnswers && explainAnswers[`choice-${index}`] && (
            <div className="pl-[35px] text-over-primary-background">
              <p
                className="text-xs"
                dangerouslySetInnerHTML={{
                  __html: formatExplanation(
                    explainAnswers[`choice-${index}`],
                    books,
                    book,
                  ),
                }}
              />
            </div>
          )}
        </div>
      )
    })
  }, [
    gridsAnswers,
    isMultipleChoice,
    selectedAnswerIndex,
    isSubject,
    answer,
    currentGridIndex,
    isCreateSubject,
    explainAnswers,
    books,
    book,
    handleChangeAnswer,
    userDetails?.tutorial,
    handleNextTourStep,
  ])

  const showTour = useMemo(() => {
    return !isLoading
  }, [isLoading])

  useEffect(() => {
    if (started) {
      setStepToSelector('.grid-info')
    }
  }, [handleNextTourStep, started, setStepToSelector])

  useEffect(() => {
    if (refference?.current) {
      refference.current.scrollTo({ top: 0, left: 0 })
    }
  }, [currentGrila, isLoading, refference])

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })

  const isTablet = useMediaQuery({ query: '(max-width: 1200px)' })

  const { coursesWithChapters } = useGetFilters()

  return (
    <>
      <div
        className={`grid-content-container mt-0 pt-1 md:pt-4 bg-container-background z-[100] px-2 md:px-[15px] pb-1 md:pb-[60px] h-full items-stretch relative w-full overflow-y-auto ${
          isSubmitted ? 'duration-500' : ''
        } ${answer && '!pb-[150px]'} `}
        ref={refference}
      >
        {endTrial ? (
          <>
            {' '}
            <div className="flex flex-col justify-center items-center gap-8 py-[25px]">
              <PremiumLocked
                lockStyle={{
                  width: '50px',
                  height: '50px',
                  color: 'rgb(244, 196, 48)',
                }}
              />
              <span className="text-over-primary-background text-center">
                Ai terminat grilele din trialul gratuit. Pentru a continua,
                trebuie să îți cumperi un abonament
              </span>
              <NavLink to={pages.app.payment} className="w-[fit-content] block">
                <Button
                  className="text-[15px] max-w-[200px] !mt-[10px] bg-primary"
                  onClick={() => {}}
                >
                  Abonamente
                </Button>
              </NavLink>
            </div>
          </>
        ) : hasSubscription !== undefined &&
          (started === false || (justStarted && !isLoading)) ? (
          <>
            <CustomTour isOpen={showTour} />
            <MDBox className="flex flex-col items-center justify-center trial-div">
              <div
                className={`text-center text-over-primary-background ${isMobile || isTablet ? 'text-[14px] p-0 pt-1 w-[95%]' : 'p-2 w-[90%] '}`}
              >
                Poți selecta setările dorite înainte să începi sau pe parcursul
                rezolvării.
              </div>

              <Button
                id="tutorial-start-now-btn"
                isLoading={isLoading}
                className="bg-btn-primary h-10 w-32 !mt-[10px]"
                onClick={() => {
                  handleStartNow(false)
                }}
              >
                Începe acum
              </Button>

              <div className="text-over-primary-background mt-4 border-t-[1px] border-solid border-grey w-[100%]" />

              {(isMobile || isTablet) && (
                <div className="text-over-primary-background text-[10px] mt-1 opacity-50">
                  * secțiunile marcate cu{' '}
                  <PremiumLocked
                    lockStyle={{
                      width: '13px',
                      height: '13px',
                      color: 'grey',
                      opacity: '1 !important',
                    }}
                  />{' '}
                  nu sunt disponibile în modul de rezolvare ales.
                </div>
              )}
              {!hasSubscription && (
                <div className="text-primary font-bold text-[10px] lg:text-[13px] mt-1">
                  {' '}
                  * doar filtrarea după {settings.materie} este disponibilă fără
                  un abonament premium{' '}
                </div>
              )}

              <FilterDialog
                hasSubscription={hasSubscription}
                filterDifficulty={filterDifficulty}
                handleSelectDifficulty={handleSelectDifficulty}
                handleTagsCheckboxChange={handleTagsCheckboxChange}
                filteredTags={filteredTags}
                handleChangeChaptersByCourse={handleChangeChaptersByCourse}
                grileTypes={grileTypes}
                handleChangeGrileTypes={handleChangeGrileTypes}
                handleChangeSolvingMode={handleChangeSolvingMode}
                solvingModes={solvingModes}
                coursesWithChapters={coursesWithChapters}
                className="w-full"
              />
            </MDBox>
          </>
        ) : !isLoading && currentGrila ? (
          <>
            <div
              className={
                answer ? 'answered-content-container' : 'content-container'
              }
            >
              {mappedCurrentGrid}
              <div
                className={` ${
                  explainAnswers ? 'flex flex-col' : 'grid'
                } choices-container !mt-[10px] md:!mt-[35px] gap-5`}
              >
                {mappedGridAnswers}
              </div>
            </div>
          </>
        ) : (
          showSkeleton && (
            <div>
              <GridSkeleton />
              <CustomTour isOpen={false} />
            </div>
          )
        )}
      </div>
    </>
  )
}

export default Content
